import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "../../api/client";
import { USERS } from "../../constants/testIds";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { InitialsAvatar } from "../../components/shared/InitialsAvatar";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { toast } from "sonner";
import { ArrowLeft, Plus, KeyRound } from "lucide-react";

export default function UserEditor() {
    const { id } = useParams();
    const isNew = id === "new";
    const nav = useNavigate();
    const [user, setUser] = useState(null);
    const [form, setForm] = useState({
        email: "", name: "", initials: "", role: "staff",
        salaried: false, active: true,
        overtime: { multiplier: 1.5, threshold_hrs_per_day: 8, weekend_multiplier: 1.5 },
        notification_prefs: { email_timesheet_reminders: true, email_rate_changes: true },
    });
    const [reason, setReason] = useState("");
    const [initialPin, setInitialPin] = useState("0000");
    const [initialPassword, setInitialPassword] = useState("");
    const [rateDialogOpen, setRateDialogOpen] = useState(false);
    const [rateForm, setRateForm] = useState({ amount: "", work_type_scope: "field", reason: "" });

    useEffect(() => {
        if (isNew) return;
        api.get(`/users/${id}`).then((r) => {
            setUser(r.data);
            setForm({
                email: r.data.email,
                name: r.data.name,
                initials: r.data.initials,
                role: r.data.role,
                salaried: r.data.salaried,
                active: r.data.active,
                overtime: r.data.overtime || form.overtime,
                notification_prefs: r.data.notification_prefs || form.notification_prefs,
            });
        });
    }, [id]);

    const save = async () => {
        try {
            if (isNew) {
                const body = {
                    email: form.email,
                    name: form.name,
                    initials: form.initials,
                    role: form.role,
                    salaried: form.salaried,
                    pin: initialPin || undefined,
                    password: initialPassword || undefined,
                };
                const { data } = await api.post("/users", body);
                toast.success("User created");
                nav(`/admin/users/${data.id}`, { replace: true });
            } else {
                const body = {
                    name: form.name,
                    initials: form.initials,
                    role: form.role,
                    salaried: form.salaried,
                    active: form.active,
                    overtime: form.overtime,
                    notification_prefs: form.notification_prefs,
                    reason: reason || undefined,
                };
                const { data } = await api.patch(`/users/${id}`, body);
                setUser(data);
                setReason("");
                toast.success("Saved");
            }
        } catch (e) {
            toast.error(apiErr(e));
        }
    };

    const addRate = async () => {
        try {
            const body = {
                amount: parseFloat(rateForm.amount),
                work_type_scope: rateForm.work_type_scope,
                reason: rateForm.reason || undefined,
            };
            const { data } = await api.post(`/users/${id}/rates`, body);
            setUser(data);
            setRateDialogOpen(false);
            setRateForm({ amount: "", work_type_scope: "field", reason: "" });
            toast.success("Rate added — prior entry closed");
        } catch (e) {
            toast.error(apiErr(e));
        }
    };

    if (!isNew && !user) return <div className="text-muted">Loading…</div>;
    const rates = user?.rates || [];

    return (
        <div data-testid={USERS.editor} className="space-y-8 max-w-4xl">
            <div className="flex items-center gap-4">
                <button
                    onClick={() => nav("/admin/users")}
                    className="text-muted hover:text-ink flex items-center gap-1.5 text-sm"
                >
                    <ArrowLeft size={16} /> Users
                </button>
            </div>

            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="flex items-center gap-5">
                    <InitialsAvatar name={form.name || "?"} initials={form.initials} size={72} />
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-muted mb-2">
                            {isNew ? "New user" : "Editing"}
                        </div>
                        <h1 className="font-extrabold tracking-[-0.03em] text-4xl leading-none">
                            {form.name || "New user"}
                        </h1>
                        <div className="text-muted mt-2 text-sm">{form.email || "—"}</div>
                    </div>
                </div>
                {!isNew && <StatusBadge status={form.role} />}
            </div>

            {/* --- Details --- */}
            <section className="km-card p-6 space-y-5">
                <h2 className="font-extrabold tracking-[-0.03em] text-xl">Profile</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                        <Label>Full name</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div>
                        <Label>Initials</Label>
                        <Input maxLength={3} value={form.initials} onChange={(e) => setForm({ ...form, initials: e.target.value.toUpperCase() })} />
                    </div>
                    <div>
                        <Label>Email</Label>
                        <Input type="email" value={form.email} disabled={!isNew} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div>
                        <Label>Role</Label>
                        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="office">Office</SelectItem>
                                <SelectItem value="staff">Staff</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-center justify-between rounded-md border border-km px-3 py-2.5">
                        <div>
                            <div className="text-sm font-medium">Salaried</div>
                            <div className="text-xs text-muted">Payroll hides $ totals</div>
                        </div>
                        <Switch checked={form.salaried} onCheckedChange={(v) => setForm({ ...form, salaried: v })} />
                    </div>
                    {!isNew && (
                        <div className="flex items-center justify-between rounded-md border border-km px-3 py-2.5">
                            <div>
                                <div className="text-sm font-medium">Active</div>
                                <div className="text-xs text-muted">Disable to block sign-in</div>
                            </div>
                            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                        </div>
                    )}
                </div>

                {isNew && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2">
                        <div>
                            <Label>Initial PIN (4 digits)</Label>
                            <Input maxLength={4} className="num" value={initialPin} onChange={(e) => setInitialPin(e.target.value.replace(/\D/g, "").slice(0, 4))} />
                        </div>
                        <div>
                            <Label>Initial password (optional, admin/office)</Label>
                            <Input type="password" value={initialPassword} onChange={(e) => setInitialPassword(e.target.value)} />
                        </div>
                    </div>
                )}

                {!isNew && (
                    <div>
                        <Label>Reason for change (audit)</Label>
                        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this change being made?" />
                    </div>
                )}

                <div className="flex justify-end pt-2">
                    <Button data-testid={USERS.saveBtn} onClick={save}>
                        {isNew ? "Create user" : "Save changes"}
                    </Button>
                </div>
            </section>

            {/* --- Overtime --- */}
            {!isNew && (
                <section className="km-card p-6 space-y-4">
                    <h2 className="font-extrabold tracking-[-0.03em] text-xl">Overtime</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                        <div>
                            <Label>Multiplier (weekday)</Label>
                            <Input
                                type="number" step="0.1" className="num"
                                value={form.overtime.multiplier}
                                onChange={(e) => setForm({ ...form, overtime: { ...form.overtime, multiplier: parseFloat(e.target.value) } })}
                            />
                        </div>
                        <div>
                            <Label>Threshold (hrs/day)</Label>
                            <Input
                                type="number" step="0.5" className="num"
                                value={form.overtime.threshold_hrs_per_day}
                                onChange={(e) => setForm({ ...form, overtime: { ...form.overtime, threshold_hrs_per_day: parseFloat(e.target.value) } })}
                            />
                        </div>
                        <div>
                            <Label>Weekend multiplier</Label>
                            <Input
                                type="number" step="0.1" className="num"
                                value={form.overtime.weekend_multiplier}
                                onChange={(e) => setForm({ ...form, overtime: { ...form.overtime, weekend_multiplier: parseFloat(e.target.value) } })}
                            />
                        </div>
                    </div>
                </section>
            )}

            {/* --- Rate history --- */}
            {!isNew && !form.salaried && (
                <section className="km-card p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="font-extrabold tracking-[-0.03em] text-xl">Rate history</h2>
                        <Button
                            data-testid={USERS.addRateBtn}
                            variant="outline"
                            size="sm"
                            onClick={() => setRateDialogOpen(true)}
                            className="gap-2"
                        >
                            <Plus size={14} /> Add rate
                        </Button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-muted border-b border-[color:var(--km-border)]">
                                    <th className="py-2 pr-4 font-medium">Amount</th>
                                    <th className="py-2 pr-4 font-medium">Scope</th>
                                    <th className="py-2 pr-4 font-medium">From</th>
                                    <th className="py-2 pr-4 font-medium">To</th>
                                    <th className="py-2 pr-4 font-medium">Set by</th>
                                    <th className="py-2 pr-4 font-medium">Reason</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...rates].sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1)).map((r, i) => (
                                    <tr key={i} className="border-b border-[color:var(--km-border)] last:border-b-0">
                                        <td className="py-2.5 pr-4 num text-ink font-medium">${r.amount}</td>
                                        <td className="py-2.5 pr-4"><StatusBadge status={r.work_type_scope === "office" ? "office" : r.work_type_scope === "field" ? "active" : "open"} /></td>
                                        <td className="py-2.5 pr-4 num text-xs text-muted">{r.effective_from?.slice(0, 10)}</td>
                                        <td className="py-2.5 pr-4 num text-xs text-muted">{r.effective_to?.slice(0, 10) || "current"}</td>
                                        <td className="py-2.5 pr-4 text-xs text-muted">{r.set_by}</td>
                                        <td className="py-2.5 pr-4 text-xs text-muted">{r.reason || "—"}</td>
                                    </tr>
                                ))}
                                {!rates.length && (
                                    <tr><td colSpan={6} className="py-6 text-center text-muted text-sm">No rates yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* --- Notifications --- */}
            {!isNew && (
                <section className="km-card p-6 space-y-4">
                    <h2 className="font-extrabold tracking-[-0.03em] text-xl">Notifications</h2>
                    <div className="space-y-3">
                        <label className="flex items-center justify-between rounded-md border border-km px-4 py-3">
                            <div>
                                <div className="text-sm">Timesheet reminders</div>
                                <div className="text-xs text-muted">Weekly emails to submit timesheets</div>
                            </div>
                            <Switch
                                checked={form.notification_prefs.email_timesheet_reminders}
                                onCheckedChange={(v) => setForm({ ...form, notification_prefs: { ...form.notification_prefs, email_timesheet_reminders: v } })}
                            />
                        </label>
                        <label className="flex items-center justify-between rounded-md border border-km px-4 py-3">
                            <div>
                                <div className="text-sm">Rate changes</div>
                                <div className="text-xs text-muted">Email when a rate is updated</div>
                            </div>
                            <Switch
                                checked={form.notification_prefs.email_rate_changes}
                                onCheckedChange={(v) => setForm({ ...form, notification_prefs: { ...form.notification_prefs, email_rate_changes: v } })}
                            />
                        </label>
                    </div>
                </section>
            )}

            {/* Add rate dialog */}
            <Dialog open={rateDialogOpen} onOpenChange={setRateDialogOpen}>
                <DialogContent data-testid={USERS.addRateDialog}>
                    <DialogHeader>
                        <DialogTitle className="font-extrabold tracking-[-0.03em] text-2xl">Add rate</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Amount (AUD/hr)</Label>
                            <Input
                                data-testid={USERS.addRateAmount}
                                type="number" step="0.01" className="num"
                                value={rateForm.amount}
                                onChange={(e) => setRateForm({ ...rateForm, amount: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Scope</Label>
                            <Select
                                value={rateForm.work_type_scope}
                                onValueChange={(v) => setRateForm({ ...rateForm, work_type_scope: v })}
                            >
                                <SelectTrigger data-testid={USERS.addRateScope}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="field">Field (FW/OW/travel)</SelectItem>
                                    <SelectItem value="office">Office (drafting/search/admin)</SelectItem>
                                    <SelectItem value="all">All (fallback)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Reason</Label>
                            <Input
                                data-testid={USERS.addRateReason}
                                value={rateForm.reason}
                                onChange={(e) => setRateForm({ ...rateForm, reason: e.target.value })}
                                placeholder="Annual review / promotion / market"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRateDialogOpen(false)}>Cancel</Button>
                        <Button data-testid={USERS.addRateSubmit} onClick={addRate}>
                            <KeyRound size={14} className="mr-2" /> Add rate
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
